import React, { useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import FieldList from "./FieldList";
import LazyFieldList from "./LazyFieldList";
import VirtualizedFieldTree from "./VirtualizedFieldTree";
import FieldContextMenu from "./FieldContextMenu";
import FieldEditContextMenu from "./FieldEditContextMenu";
import withUpdateStripes from "../misc/withUpdateStripes";
import { IAppState } from "../../types/stores/index";
import {
    setPrevEditable,
    requestLazyFieldTree,
    switchToViewModeCache,
    clearExpandedFolders,
    performFieldLabelSearch,
    clearFieldLabelSearchResults,
} from "../../actions/fieldTreeActions";

type Props = {
    TreeType: number;
    showHiddenFields?: boolean;
    isPIIOnly?: boolean;
    editable?: boolean;
    ctrlShift: boolean;
    isModal?: boolean;
    showTabs?: boolean;
    // Injected by HOC
    updateStripes?: () => void;
};

const FieldTree: React.FC<Props> = ({
    TreeType,
    showHiddenFields,
    isPIIOnly,
    editable,
    updateStripes,
    ctrlShift,
    showTabs,
    isModal,
}: Props) => {
    const dispatch = useDispatch();

    // Check if lazy loading is enabled
    const useLazyLoading = useSelector((state: IAppState) => {
        const enabledFeatures = state.session?.enabledFeatures || [];
        return enabledFeatures.includes("lazy-loading-fieldtree") && state.search.treeSearch == "";
    });

    const prevEditable = useSelector((state: IAppState) => state.vars.prevEditable);
    const cachedFieldsView = useSelector((state: IAppState) => state.vars.fieldsView);

    // When the dev virtualization flag is on, render the whole field tree through a windowed
    // list instead of mounting every node
    const virtualizationFlagOn = useSelector((state: IAppState) => {
        const enabledFeatures = state.session?.enabledFeatures || [];
        return enabledFeatures.includes("virtualized-fieldtree");
    });

    const useVirtualized = virtualizationFlagOn;
    const fieldLabelState = useSelector(
        (state: IAppState) => ({
            selectedFieldLabelIds: state.search.selectedFieldLabels,
            selectedFieldLabelId3: state.search.selectedFieldLabels3,
        }),
        shallowEqual
    );

    const selectedFieldLabelIds = fieldLabelState.selectedFieldLabelIds;

    // Field label search - only when lazy loading is enabled
    useEffect(() => {
        if (!useLazyLoading) {
            return;
        }

        if (selectedFieldLabelIds.length > 0) {
            dispatch(performFieldLabelSearch(selectedFieldLabelIds));
        } else {
            dispatch(clearFieldLabelSearchResults());
        }
    }, [selectedFieldLabelIds, dispatch, useLazyLoading]);

    useEffect(() => {
        if (!useLazyLoading) {
            return;
        }

        const currentEditable = editable === true;
        const previousEditable = prevEditable === true;
        const hasTransitioned = prevEditable !== undefined && previousEditable !== currentEditable;

        if (hasTransitioned) {
            dispatch(clearExpandedFolders);
            // Switch to view mode when transitioning from edit to view mode
            if (previousEditable && !currentEditable) {
                if (!cachedFieldsView) {
                    dispatch({ type: "CLEAR_FIELDS_FOR_MODE_SWITCH" });
                    dispatch(requestLazyFieldTree());
                }
                dispatch(switchToViewModeCache());
            }
        }

        dispatch(setPrevEditable(currentEditable));
    }, [editable, prevEditable, dispatch, useLazyLoading, cachedFieldsView]);

    useEffect(() => {
        updateStripes && updateStripes();
    }, [updateStripes]);

    const contextMenu = editable ? <FieldEditContextMenu /> : <FieldContextMenu />;

    if (useVirtualized) {
        return (
            <div className="field-tree" style={{ overflow: "hidden" }}>
                {contextMenu}
                <VirtualizedFieldTree
                    TreeType={TreeType}
                    editable={Boolean(editable)}
                    showHiddenFields={Boolean(showHiddenFields)}
                    isPIIOnly={Boolean(isPIIOnly)}
                    ctrlShift={ctrlShift}
                    isModal={Boolean(isModal)}
                    showTabs={showTabs || false}
                    tabType={0}
                    lazy={useLazyLoading}
                />
            </div>
        );
    }

    return (
        <div className="field-tree">
            {useLazyLoading ? (
                <div style={{ position: "relative" }}>
                    {contextMenu}
                    <LazyFieldList
                        key="#"
                        fieldKey="#"
                        treeViewOnUpdate={() => updateStripes && updateStripes()}
                        TreeType={TreeType}
                        showAllDuringSearch={false}
                        editable={editable ?? false}
                        showHiddenFields={showHiddenFields ?? false}
                        isPIIOnly={isPIIOnly ?? false}
                        ctrlShift={ctrlShift}
                        isModal={isModal ?? false}
                        showTabs={showTabs ?? false}
                        tabType={0}
                    />
                </div>
            ) : (
                <>
                    <FieldList
                        key="#"
                        fieldKey="#"
                        treeViewOnUpdate={updateStripes!}
                        TreeType={TreeType}
                        isPIIOnly={isPIIOnly || false}
                        editable={Boolean(editable)}
                        showHiddenFields={Boolean(showHiddenFields)}
                        ctrlShift={ctrlShift}
                        showAllDuringSearch={false}
                        isModal={Boolean(isModal)}
                        showTabs={showTabs || false}
                        tabType={0}
                    />
                    {contextMenu}
                </>
            )}
        </div>
    );
};

export default withUpdateStripes(FieldTree);
